
import { Session } from '@supabase/supabase-js';
import { createLogger } from '../utils/loggingUtils';

const logger = createLogger('sessionState');

// Keep track of the current session
let currentSession: Session | null = null;
let lastRefreshAttempt = 0;
let refreshAttempts = 0;
const MIN_REFRESH_INTERVAL = 60000; // 1 minute between refresh attempts (increased from 30s)
const MAX_REFRESH_ATTEMPTS = 5;

// Reset the session state
export const resetSessionState = () => {
  logger.debug('Session state reset'); // Changed from info to debug
  lastRefreshAttempt = 0;
  refreshAttempts = 0;
  currentSession = null;
};

// Get current session
export const getCurrentSession = (): Session | null => {
  return currentSession;
};

// Update current session
export const updateCurrentSession = (session: Session | null): void => {
  // Only update if there's actually a change
  if (JSON.stringify(currentSession) !== JSON.stringify(session)) {
    currentSession = session;
  }
};

// Get and update refresh attempt state
export const getRefreshAttemptsState = (): { 
  lastAttempt: number; 
  count: number; 
  exceedsLimit: boolean;
  shouldApplyBackoff: boolean;
  getBackoffTime: () => number;
} => {
  const now = Date.now();
  const attemptsExceedMax = refreshAttempts > MAX_REFRESH_ATTEMPTS;
  
  return {
    lastAttempt: lastRefreshAttempt,
    count: refreshAttempts,
    exceedsLimit: attemptsExceedMax,
    shouldApplyBackoff: attemptsExceedMax,
    getBackoffTime: () => {
      if (attemptsExceedMax) {
        // Apply exponential backoff
        return Math.min(1000 * Math.pow(2, refreshAttempts - MAX_REFRESH_ATTEMPTS), 60000);
      }
      return 0;
    }
  };
};

// Update refresh attempt tracking
export const trackRefreshAttempt = (success: boolean = false): void => {
  lastRefreshAttempt = Date.now();
  
  if (success) {
    refreshAttempts = 0;
  } else {
    refreshAttempts++;
  }
  
  logger.debug(`Session refresh attempt tracked: ${success ? 'successful' : 'failed'} (attempt #${refreshAttempts})`); // Changed from info to debug
};

// Should rate limit a refresh attempt?
export const shouldRateLimitRefresh = (): boolean => {
  const now = Date.now();
  return now - lastRefreshAttempt < MIN_REFRESH_INTERVAL;
};
